import { useState } from 'react';
import type { RawEventFlagRow } from '@halo/web-types';
import { useUpsertFlags } from './useHaloV2';

const FLAG_KEYS = [
  'is_promo_day',
  'is_stockout',
  'is_restock',
  'is_new_product_launch',
  'is_major_email_send',
  'is_major_sms_send',
  'is_prime_day',
  'is_bfcm',
  'is_holiday',
  'is_press_hit',
  'is_creator_drop',
  'is_retail_launch',
  'is_price_change',
] as const;

type FlagKey = (typeof FLAG_KEYS)[number];

const EMPTY_ROW: Omit<RawEventFlagRow, 'date'> = {
  is_promo_day: 0,
  promo_type: null,
  discount_depth: null,
  is_stockout: 0,
  is_restock: 0,
  is_new_product_launch: 0,
  is_major_email_send: 0,
  is_major_sms_send: 0,
  is_prime_day: 0,
  is_bfcm: 0,
  is_holiday: 0,
  is_press_hit: 0,
  is_creator_drop: 0,
  is_retail_launch: 0,
  is_price_change: 0,
};

interface FlagEditorProps {
  storeId: number;
  date: string;
  existing: RawEventFlagRow | undefined;
  onSaved: () => void;
}

export function FlagEditor({ storeId, date, existing, onSaved }: FlagEditorProps) {
  const [row, setRow] = useState<Omit<RawEventFlagRow, 'date'>>(() => {
    if (!existing) return EMPTY_ROW;
    const { date: _date, ...rest } = existing;
    void _date;
    return rest;
  });
  const upsert = useUpsertFlags();

  const save = () => upsert.mutate({ storeId, flags: [{ date, ...row }] }, { onSuccess: onSaved });

  return (
    <div className="mt-2 space-y-1">
      <div className="grid grid-cols-2 gap-x-2">
        {FLAG_KEYS.map((key: FlagKey) => (
          <label
            key={key}
            className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-300"
          >
            <input
              type="checkbox"
              checked={row[key] === 1}
              onChange={(e) => setRow({ ...row, [key]: e.target.checked ? 1 : 0 })}
            />
            {key.replace(/^is_/, '').replace(/_/g, ' ')}
          </label>
        ))}
      </div>
      <input
        className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs"
        placeholder="promo_type (optional)"
        value={row.promo_type ?? ''}
        onChange={(e) =>
          setRow({ ...row, promo_type: e.target.value === '' ? null : e.target.value })
        }
      />
      <input
        className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs"
        placeholder="discount_depth 0-100 (optional)"
        type="number"
        min={0}
        max={100}
        value={row.discount_depth ?? ''}
        onChange={(e) =>
          setRow({ ...row, discount_depth: e.target.value === '' ? null : Number(e.target.value) })
        }
      />
      <button
        type="button"
        disabled={upsert.isPending}
        onClick={save}
        className="rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-3 py-1 text-xs font-medium disabled:opacity-50"
      >
        Save day
      </button>
      {upsert.isError ? (
        <p className="text-[11px] text-red-500">{upsert.error?.message ?? 'Flag upsert failed'}</p>
      ) : null}
      <p className="text-[10px] text-gray-400">
        Saves the FULL day (unchecked flags are cleared). Re-run baselines to apply changes.
      </p>
    </div>
  );
}
